import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
// W124.4 · 2026-05-26 · dynamic header title resolves from route segment.
import { getTitleForRoute } from '@/lib/route-titles';
import { Avatar } from '@/components/ui/Avatar';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageToggle } from '@/components/LanguageToggle';
// Wave cmdk-unified · CommandPalette (Wave 12.3, legacy) removed from TopBar.
// Cmd+K is now handled exclusively by AppShell → CommandBar (Wave eeeee).
// TopBar's search button calls `onOpenSearch` from AppShell instead of
// maintaining its own paletteOpen state.
import { InboxBell } from '@/components/messaging/InboxBell';
import { ChatLauncher } from '@/components/chat-instant/ChatLauncher';
import { NotificationsBell } from '@/components/notifications/NotificationsBell';
import { PresencePill } from '@/components/presence/PresencePill';
import { PrumoPill } from '@/components/prumo/PrumoPill';
// FEATURE C2 · 2026-05-21 — pill que mostra status offline + outbox count.
import { OfflineIndicator } from '@/components/layout/OfflineIndicator';
import { ProjectSwitcher } from '@/components/projects/ProjectSwitcher';
import { useAuth } from '@/store/auth.store';
import { api } from '@/api/client';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';
// Wave help-tour-mvp · 2026-05-20 — trigger MVP interactive tour from
// the account dropdown menu ("Refazer tour"). Tour itself lives in
// AppShell; openTour() dispatches a window event. resetTourCompleted
// limpa o flag de localStorage pra reabrir do zero.
import { openTour } from '@/components/onboarding/useTourTrigger';
import { resetTourCompleted } from '@/components/onboarding/Tour';

/**
 * Wave 100 v4 — TopBar impeccable polish (2026-05-17).
 *
 * Changes from v3:
 *   - Builder logo (house SVG) restored at left edge — branding always visible.
 *   - All icon buttons unified to h-11 w-11 (44pt, iOS HIG) with 18px interior icon.
 *   - Consistent gap-3 md:gap-4 in right cluster.
 *   - ThemeToggle, InboxBell, NotificationsBell all same 40x40 target.
 *   - InboxBell upgraded from 36px → 40px, icon from 16px → 18px.
 *   - Avatar name shows on sm+ (desktop).
 *   - Mobile: logo + ProjectSwitcher compact + InboxBell + Avatar always shown.
 *     Prumo / Presence / Theme / Notifications collapse to sm-hidden.
 *   - transition-colors duration-150 everywhere (never transition: all).
 *   - No scale-from-zero entries. No sluggish easing. No border-l side-stripe.
 */

/** Builder house mark — 24×24 SVG inline, no external fetch. */
function BuilderMark({ className }: { className?: string }) {
  return (
    <svg
      data-testid="builder-logo"
      aria-label="Builder"
      role="img"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      {/* Roof */}
      <path
        d="M3 10.5L12 3l9 7.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Walls */}
      <path
        d="M5 10.5V20a1 1 0 0 0 1 1h4v-5h4v5h4a1 1 0 0 0 1-1V10.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function TopBar({
  onOpenSidebar,
  sidebarMounted,
  onOpenSearch,
}: {
  onOpenSidebar: () => void;
  sidebarMounted: boolean;
  /** Wave cmdk-unified · callback from AppShell to open the CommandBar.
   *  TopBar no longer owns Cmd+K state — it delegates upward so exactly
   *  one listener exists (AppShell's window-level handler). */
  onOpenSearch?: () => void;
}) {
  const { t, locale } = useT();
  const isEn = locale === 'en';
  const user = useAuth((s) => s.user);
  const clear = useAuth((s) => s.clear);
  const navigate = useNavigate();
  const location = useLocation();
  // W124.4 · projectId is opt-in (only set when matched by route shell).
  // useParams safely returns {} outside a project route — no crash.
  const params = useParams<{ projectId?: string }>();
  const headerTitle = getTitleForRoute(location.pathname, params.projectId);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const onLogout = async () => {
    setOpen(false);
    try {
      await api.logout();
    } catch {
      // Even if the logout API call fails (offline, expired token), we still
      // want to clear the client-side session and bounce to /login.
    }
    clear();
    navigate('/login', { replace: true });
  };

  // Crew+ get the palette; clients/brokers stay on the dedicated portal
  // and don't see global search.
  const canUsePalette =
    !!user && user.role !== 'client' && user.role !== 'broker';

  return (
    <header
      className={cn(
        'sticky top-0 z-30 border-b border-line',
        'bg-surface/80 backdrop-blur-md supports-[backdrop-filter]:bg-surface/60',
      )}
    >
      <div className="flex h-14 items-center justify-between px-3 md:h-16 md:px-6">

        {/* ── LEFT: logo + hamburger + project switcher ── */}
        <div className="flex items-center gap-3">
          {/* Builder logo mark — always visible, links to home. */}
          <Link
            to="/"
            aria-label="Builder — ir para home"
            className={cn(
              'pressable focus-ring flex h-11 w-11 shrink-0 items-center justify-center rounded-lg',
              'text-brand-500 hover:bg-brand-500/8 transition-colors duration-150',
            )}
          >
            <BuilderMark />
          </Link>

          {/* Mobile hamburger — only on small viewports where SideNav is hidden. */}
          {sidebarMounted && (
            <button
              type="button"
              onClick={onOpenSidebar}
              aria-label={isEn ? 'Open menu' : 'Abrir menu'}
              className={cn(
                'pressable focus-ring inline-flex h-11 w-11 items-center justify-center rounded-lg',
                'text-ink-muted hover:bg-surface-2 hover:text-ink transition-colors duration-150 md:hidden',
              )}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            </button>
          )}

          {/* Project switcher — visible when authenticated. */}
          <ProjectSwitcher />
        </div>

        {/* ── CENTER: dynamic route title (W124.4) ──────────────────────────
            Replaces the implicit static "Obra" rendered by the shell on every
            project sub-route. Resolves the active mesa / page from
            `getTitleForRoute(pathname, projectId)`.

            Hidden on mobile because MobileProjectHeader (W124.5 territory)
            owns the small-viewport title row. Desktop: visible from sm+ so
            the PM always sees which mesa is active.

            min-w-0 + truncate keeps long admin titles from pushing the right
            cluster off-screen on narrow desktops. ──────────────────────── */}
        <div
          className="hidden sm:flex flex-1 min-w-0 px-4 justify-center"
          data-testid="topbar-title-slot"
        >
          <h1
            data-testid="topbar-title"
            className="truncate text-[15px] font-semibold text-ink leading-none"
          >
            {headerTitle}
          </h1>
        </div>

        {/*
          ── RIGHT cluster ──────────────────────────────────────────────────
          All icon buttons: h-11 w-11 (44pt iOS HIG). Interior icons: 18x18.
          Mobile (<sm): only search pill, InboxBell, avatar visible.
          Desktop (≥sm): full cluster — Prumo, Presence, Theme, Inbox,
          Notifications, Avatar.
          ──────────────────────────────────────────────────────────────────
        */}
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4">

          {/* Search palette trigger — visible when user can search.
              Wave cmdk-unified: delegates to AppShell's onOpenSearch
              so the CommandBar (Wave eeeee) opens, not the legacy
              CommandPalette. One Cmd+K handler, one experience. */}
          {canUsePalette && (
            <button
              type="button"
              onClick={() => onOpenSearch?.()}
              aria-label={t.wave12.search.openPalette}
              title={t.wave12.search.openPalette}
              className={cn(
                'pressable focus-ring flex items-center gap-2 rounded-full border border-line',
                'bg-surface-2 px-3 py-1.5 text-xs text-ink-muted',
                'hover:text-ink hover:bg-surface-3 transition-colors duration-150',
                // Mobile: icon-only (44pt min touch). Label + kbd on ≥ sm.
                'min-h-[44px] sm:min-h-0',
              )}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
                <path d="M14 14l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
              <span className="hidden sm:inline">{t.wave12.search.label}</span>
              <kbd
                className={cn(
                  'hidden sm:inline-flex h-5 items-center rounded border border-line',
                  'bg-surface px-1 text-[10px] font-medium',
                )}
              >
                {t.wave12.search.shortcut}
              </kbd>
            </button>
          )}

          {/* Wave 43 · Bug 5 — Housy concierge icon button.
              Dispatches housy:toggle → HouseAgent opens its chat panel.
              Keyboard shortcut Ctrl+H / Cmd+H also works (handled inside
              HouseAgent). Hidden on mobile (cramped), accessible via the
              housy:toggle event from other surfaces. */}
          {user && canUsePalette && (
            <span className="hidden sm:contents">
              <button
                type="button"
                aria-label={isEn ? 'Open Housy — site assistant (Ctrl+H)' : 'Abrir Housy — assistente de obra (Ctrl+H)'}
                title={isEn ? 'Housy · site assistant (Ctrl+H)' : 'Housy · assistente de obra (Ctrl+H)'}
                onClick={() => window.dispatchEvent(new CustomEvent('housy:toggle'))}
                className={cn(
                  'pressable focus-ring flex h-11 w-11 items-center justify-center rounded-lg',
                  'text-ink-muted hover:bg-surface-2 hover:text-brand-500 transition-colors duration-150',
                )}
              >
                {/* House icon — distinct from BuilderMark (no cutout door,
                    softer stroke). 18×18 to match other TopBar icons. */}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 10.5L12 3l9 7.5" />
                  <path d="M5 10.5V20a1 1 0 0 0 1 1h4v-4h4v4h4a1 1 0 0 0 1-1V10.5" />
                  <circle cx="12" cy="14" r="1.5" fill="currentColor" stroke="none" />
                </svg>
              </button>
            </span>
          )}

          {/* Secondary controls: Prumo, Presence, Theme — hidden on mobile.
              Remain accessible via the avatar dropdown on mobile.
              W158 · 2026-06-02 — Prumo (e o atalho CMD-SHIFT-A) é superfície
              de equipe, não do portal externo. Esconde para cliente/corretor.
              ROUTE-02 · 2026-06-03 — A fila de ações sugeridas é uma superfície
              de operador (aprovação/autonomia). Restrita a admin + gestor;
              mestre/pedreiro não veem. */}
          {!!user && (user.role === 'admin' || user.role === 'project_manager') && (
            <span className="hidden sm:contents"><PrumoPill /></span>
          )}
          {user && <span className="hidden sm:contents"><PresencePill /></span>}

          {/* FEATURE C2 · 2026-05-21 — Offline + outbox indicator.
              Visível em mobile também (canteiro 1G é onde o estado
              importa mais). Auto-some quando online E outbox vazio. */}
          {user && <OfflineIndicator />}

          {/* ThemeToggle — same 44x44 target (Codex round-7 sweep). */}
          <span
            data-testid="theme-toggle-wrapper"
            className="hidden sm:contents"
          >
            <ThemeToggle className="h-11 w-11" />
          </span>

          {/* ChatLauncher — instant chat (Wave chat-instant).
              Renderizado IMEDIATAMENTE antes do InboxBell, mas com gap
              próprio (separator visual) e ícone distinto (balão vs
              envelope) pra o PM diferenciar velocidade esperada de
              resposta (mensagem rápida vs email formal). JP foi
              explícito: "misturar email com mensagens instantânea não é
              uma boa ideia". */}
          {user && <ChatLauncher />}

          {/* InboxBell — 40x40, icon 18px. Always visible. */}
          {user && <InboxBell />}

          {/* NotificationsBell — 40x40, icon 18px. Hidden on mobile. */}
          {user && <span className="hidden sm:contents"><NotificationsBell /></span>}

          {/* Avatar + name dropdown */}
          {user && (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={isEn ? `Account menu: ${user.fullName}` : `Menu de conta: ${user.fullName}`}
                className={cn(
                  'pressable focus-ring flex min-h-[44px] items-center gap-2 rounded-full border border-line bg-surface-2 p-0.5 pr-2',
                  'transition-colors duration-150',
                )}
              >
                <Avatar name={user.fullName} src={user.avatarUrl} role={user.role} size="sm" />
                <span
                  data-testid="topbar-user-name"
                  className="hidden sm:inline text-sm font-medium text-ink"
                >
                  {user.fullName.split(' ')[0]}
                </span>
              </button>

              {open && (
                // listen-to-JP-fix · 2026-05-20 — added z-50 (was implicit
                // z-auto). Without it the dropdown competed with other
                // `relative` siblings via DOM-order paint, which let the
                // InboxBell panel and ProjectSwitcher dropdown bleed
                // through. All TopBar dropdowns are now z-50 (see audit
                // table in handoff notes).
                <div
                  role="menu"
                  data-testid="topbar-avatar-dropdown"
                  style={{ transformOrigin: 'top right' }}
                  className={cn(
                    'absolute right-0 mt-2 w-60 overflow-hidden rounded-xl border border-line z-50',
                    'bg-surface-2 shadow-pop dark:shadow-pop-dark animate-scale-in',
                  )}
                >
                  <div className="border-b border-line px-3 py-2.5">
                    <p className="truncate text-sm font-medium text-ink">{user.fullName}</p>
                    <p className="truncate text-xs text-ink-muted">{user.email}</p>
                  </div>

                  <Link
                    to="/settings"
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    className="flex min-h-[44px] items-center px-3 py-2 text-sm text-ink hover:bg-surface-3"
                  >
                    {t.nav.settings}
                  </Link>
                  <Link
                    to="/ajuda"
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    className="flex min-h-[44px] items-center px-3 py-2 text-sm text-ink hover:bg-surface-3"
                  >
                    {t.nav.ajuda}
                  </Link>

                  {/* Wave help-tour-mvp · 2026-05-20 — relaunch tour.
                      Resets the localStorage flag so the next auto-trigger
                      (e.g. after creating a project) also fires again. */}
                  <button
                    type="button"
                    role="menuitem"
                    data-testid="topbar-retake-tour"
                    onClick={() => {
                      setOpen(false);
                      resetTourCompleted();
                      openTour();
                    }}
                    className="flex min-h-[44px] w-full items-center px-3 py-2 text-left text-sm text-ink hover:bg-surface-3"
                  >
                    {t.tour.retake}
                  </button>

                  <div className="border-t border-line px-3 py-3">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-subtle">
                      {t.nav.language}
                    </p>
                    <LanguageToggle variant="inline" />
                  </div>

                  <button
                    type="button"
                    role="menuitem"
                    onClick={onLogout}
                    className="flex min-h-[44px] w-full items-center border-t border-line px-3 py-2.5 text-left text-sm font-medium text-ink hover:bg-surface-3"
                  >
                    {t.auth.logout}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Wave cmdk-unified · CommandPalette removed from TopBar.
          CommandBar (Wave eeeee) is the single Cmd+K surface, mounted
          in AppShell and opened via the onOpenSearch prop above. */}
    </header>
  );
}
